import {
  IModelReviewProps,
  ModelReview,
} from 'app/shared/components/ModelReview/ModelReview';
import { useRouter } from 'next/router';
import { useTranslate } from 'app/shared/components/Translation/hooks/useTranslate/useTranslate';
import { IModelSpecificCTAProps } from 'app/shared/components/ModelReview/ModelSpecificCTA/ModelSpecificCTA';
import { MediaType } from 'app/shared/components/MediaGallery/utils';
import qs from 'query-string';
import { IRatingProps } from 'app/shared/components/ModelReview/Rating/Rating';
import {
  ITableOfContentsProps,
  ITableOfContentsSection,
} from 'app/shared/components/ModelReview/TableOfContents/TableOfContents';
import { IReviewSectionProps } from 'app/shared/components/ModelReview/ReviewSection/ReviewSection';
import { ISearchPreviewBoxProps } from 'app/shared/components/ModelReview/SearchPreviewBox/SearchPreviewBox';
import {
  getStaticPathsSlugArray,
  getStaticPropsSlugArray,
  IPageData,
  transformToImageURL,
} from 'app/utils/kentico';
import {
  getPopularReviews,
  getReviewPageBySlug,
  getReviewPagesSlugs,
  ICarReviewLink,
} from 'app/apiCalls/kentico';
import KenticoPage from 'app/containers/KenticoPage/KenticoPage';
import { MAKER_MAP } from 'app/apiCalls/vehicle';

interface IReviewPage extends IPageData {
  popularModels: ICarReviewLink[];
}

const ReviewPage = ({ pageData, popularModels }: IReviewPage) => {
  const modelReviewProps = {
    ...extractReviewData(useRouter(), pageData, useTranslate()),
    otherModelsReviewsSection: { popularModels },
  };
  const pageIdPrefix = 'REVIEW_PAGE.';
  return (
    <KenticoPage pageData={pageData} pageIdPrefix={pageIdPrefix}>
      <ModelReview {...modelReviewProps} />;
    </KenticoPage>
  );
};

function extractReviewData(
  router,
  pageData,
  translate,
): Omit<IModelReviewProps, 'otherModelsReviewsSection'> {
  const { elements, system } = pageData;
  return {
    makeTitle: extractMakeTitleData(elements, system),
    modelSpecificCta: extractModelSpecificCTAData(elements),
    rating: extractRatingData(elements),
    tableOfContents: extractTableOfContentsData(elements, translate),
    overallReviewSection: extractReviewSectionData({
      element: elements.overall_verdict,
      slug: getOverallSlug(translate),
    }),
    searchPreview: extractSearchPreviewData(elements),
    onTheInsideReviewSection: extractReviewSectionData({
      element: elements.on_the_inside,
      slug: getInsideSlug(translate),
    }),
    drivingReviewSection: extractReviewSectionData({
      element: elements.driving,
      slug: getDrivingSlug(translate),
    }),
    costToRunReviewSection: extractReviewSectionData({
      element: elements.how_much_does_it_cost_to_run,
      slug: getCostToRunSlug(translate),
    }),
    pricesReviewSection: extractReviewSectionData({
      element: elements.prices_versions_and_specification,
      slug: getPricesSlug(translate),
    }),
  };
}

function extractMakeTitleData(elements, system): any {
  const [make] = elements.cars.value;
  const slugMake = make.codename.replaceAll('_', '-');
  return {
    heading: system.name,
    make: MAKER_MAP[slugMake] || slugMake,
  };
}

function extractModelSpecificCTAData(elements): IModelSpecificCTAProps {
  return {
    launchYear: elements.launch_year.value,
    bodyType: elements.body_type.value,
    fuelType: elements.fuel_type.value,
    ctaButton: extractCtaButton(elements),
    imageGallery: elements.image_gallery.value.map(image => {
      return {
        mediaType: MediaType.IMAGE,
        original: { src: image.url },
        thumbnail: { src: image.url },
        alt: image.description,
      };
    }),
  };
}

function extractCtaButton(elements) {
  const [make, model] = elements.cars.value;
  const makeQuery = make.codename.replaceAll('_', '-');
  const modelQuery = model.codename
    .replaceAll('_', '-')
    .replaceAll(`${makeQuery}-`, '');
  const searchQueryParameters = qs.stringify({
    make: makeQuery,
    model: modelQuery,
  });
  const fallbackSearchQueryParameters = qs.stringify({
    make: makeQuery,
    modelFamily: modelQuery,
  });
  return {
    label: `${make.name} ${model.name}`,
    searchQueryParameters,
    fallbackSearchQueryParameters,
  };
}

function extractRatingData(elements): IRatingProps {
  return {
    rating: elements.heycar_rating.value,
    summary: elements.review_quote.value,
    proBulletsContent: elements.best_bits.value,
    conBulletsContent: elements.not_so_great.value,
  };
}

function extractTableOfContentsData(
  elements,
  translate,
): ITableOfContentsProps {
  return {
    items: [
      _extractHeading(elements.overall_verdict, getOverallSlug(translate)),
      _extractHeading(elements.on_the_inside, getInsideSlug(translate)),
      _extractHeading(elements.driving, getDrivingSlug(translate)),
      _extractHeading(
        elements.how_much_does_it_cost_to_run,
        getCostToRunSlug(translate),
      ),
      _extractHeading(
        elements.prices_versions_and_specification,
        getPricesSlug(translate),
      ),
    ].filter(i => !!i),
  };

  function _extractHeading(
    { linkedItems },
    sectionSlug,
  ): ITableOfContentsSection {
    if (linkedItems.length === 0) {
      return null;
    }
    return {
      headingLevel: 2,
      heading: extractMainHeading({ linkedItems }),
      sectionSlug,
    };
  }
}

function extractMainHeading(element) {
  const mainSection = getMainSection(element);
  return mainSection.elements.section_header.value;
}

function extractReviewSectionData({ element, slug }): IReviewSectionProps {
  const [mainSection, ...otherSections] = splitSections(element);

  return {
    slug,
    mainSection: extractMainSection(mainSection.elements),
    additionalSections: otherSections.map(s =>
      extractSecondarySection(s.elements),
    ),
  };

  function splitSections(element) {
    const mainSection = getMainSection(element);
    return [mainSection, ...getSecondarySections(element, mainSection)];
  }

  function extractMainSection(mainSectionElements) {
    return {
      heading: mainSectionElements.section_header.value,
      content:
        mainSectionElements.body_content?.value ||
        mainSectionElements.section_content.value,
      summary: mainSectionElements.summary?.value,
      image: transformToImageURL(mainSectionElements.image),
    };
  }

  function extractSecondarySection(elements) {
    return {
      heading: elements.section_header.value,
      content: elements.section_content.value,
    };
  }
}

function extractSearchPreviewData(elements): ISearchPreviewBoxProps {
  return {
    heading: elements.marketing_block_title.value,
    content: elements.marketing_block_features.value,
    ctaButton: extractCtaButton(elements),
  };
}

function getMainSection({ linkedItems }) {
  return (
    linkedItems.find(i => i.system.type === 'page_builder_main_section') ||
    linkedItems[0]
  );
}

function getSecondarySections({ linkedItems }, mainSection) {
  return linkedItems.filter(i => i.system.id !== mainSection.system.id);
}

function getOverallSlug(translate) {
  return translate({
    id: 'shared.components.reviews.overall',
    defaultMessage: 'overall',
  });
}

function getInsideSlug(translate) {
  return translate({
    id: 'shared.components.reviews.inside',
    defaultMessage: 'inside',
  });
}

function getDrivingSlug(translate) {
  return translate({
    id: 'shared.components.reviews.driving',
    defaultMessage: 'driving',
  });
}

function getCostToRunSlug(translate) {
  return translate({
    id: 'shared.components.reviews.costRun',
    defaultMessage: 'costToRun',
  });
}

function getPricesSlug(translate) {
  return translate({
    id: 'shared.components.reviews.prices',
    defaultMessage: 'prices',
  });
}

const getStaticProps = async ({ params }) => {
  const [make] = params.slug;
  const [staticProps, popularModels] = await Promise.all([
    getStaticPropsSlugArray(params, getReviewPageBySlug),
    getPopularReviews(make).catch(() => [] as ICarReviewLink[]),
  ]);
  return {
    ...staticProps,
    props: { ...staticProps.props, popularModels },
  };
};

const getStaticPaths = async () => {
  return getStaticPathsSlugArray(getReviewPagesSlugs);
};

export { ReviewPage, getStaticProps, getStaticPaths };
